/**
 * Ticket references — Step 41. Display is `#INC-1042` / `#SR-1042` (later
 * `#PRB-` / `#CHN-`): the PREFIX derives from the ticket's CURRENT type, the
 * NUMBER never changes — which is what keeps Step 37's type conversion free
 * (every old reference still resolves; only the letters change). The email
 * subject token stays prefix-free (`[#1042]`) so threading survives conversion.
 *
 * Shared because the UI renders refs and the API parses them out of search
 * queries — one table, no drift.
 */
export const TYPE_PREFIX: Record<string, string> = {
  incident: 'INC',
  service_request: 'SR',
  problem: 'PRB',
  change: 'CHN',
  /** Step 54 — a major incident is a TYPE, not a flag. A flag says "this one
   *  is big"; a type says "this one is a different THING with different
   *  fields" — an outage kind, a business impact, a customer count and a
   *  review afterwards. Kai's own tenant models it this way. */
  major_incident: 'MI',
  /** Step 46 — a task is a ticket TYPE, not a child entity. Kai: *"I want
   *  tasks to appear as tickets, with a group and assignee, but pretty much
   *  nothing else"*, and *"someone should be able to create a task ticket to
   *  do something unrelated to a ticket."* */
  task: 'TSK',
};

export function ticketRef(type: string, number: number): string {
  return `#${TYPE_PREFIX[type] ?? 'TKT'}-${number}`;
}

/** "#INC-1042" | "INC-1042" | "sr-1042" | "#1042" | "1042" → 1042; else null.
 *  The prefix is accepted but NOT used to filter type: someone pasting an old
 *  #INC ref for a ticket since converted to an SR must still find it. */
export function parseRefQuery(q: string): number | null {
  const m = q.trim().match(/^#?(?:[A-Za-z]{2,4}-)?(\d{1,9})$/);
  return m ? Number(m[1]) : null;
}

/**
 * The human label for a ticket type. One table, because four places were
 * spelling these out and a change was rendering as "Incident" in the one that
 * had not been updated (Kai: *"why is a change type an incident"*).
 */
export const TYPE_LABEL: Record<string, string> = {
  incident: 'Incident',
  service_request: 'Service request',
  task: 'Task',
  problem: 'Problem',
  change: 'Change',
  major_incident: 'Major incident',
};

/**
 * Which types a ticket may be CONVERTED to — Step 37's rule, as a DIRECTED
 * graph since Step 54.
 *
 * It was one symmetric set, `{incident, service_request}`, and everything
 * outside it converted to nothing. That was right while the only conversion
 * was reclassification. Then "promote to major incident" arrived, which IS a
 * conversion — and adding `major_incident` to that set would have quietly
 * legalised **service request → major incident**, which is not a promotion
 * but a category error: an outage is not a request somebody submitted.
 *
 * So each type states what it may become, and the asymmetry is the point:
 *
 *   incident        ⇄ service request   reclassified, same work (Step 37)
 *   incident        ⇄ major incident    declare, and stand back down again
 *   service request → major incident    NO — see above
 *
 * Everything else still converts to nothing. A change has a plan, a risk and
 * a lifecycle; a problem has five ITIL sections; a task has none of it and a
 * parent instead. Converting between them would orphan the type's own record
 * and drop the ticket into a lifecycle it has never been through — and the
 * status it is sitting in might not even exist for the new type.
 */
const CONVERSIONS: Record<string, string[]> = {
  incident: ['service_request', 'major_incident'],
  service_request: ['incident'],
  // Demotion is allowed and matters: a major incident declared at 03:00 that
  // turns out to be one team's printer has to be able to stop being one,
  // without losing the number, the thread or the hour of notes on it.
  major_incident: ['incident'],
};

/** The current type is always first, so a picker renders it as the selected
 *  value rather than falling through to whatever option happens to come
 *  first — the bug this function was originally written for. */
export function convertibleTypes(from: string): string[] {
  return [from, ...(CONVERSIONS[from] ?? [])];
}

/** Null when the conversion is allowed, else why not. */
export function typeConversionError(from: string, to: string): string | null {
  if (from === to) return null;
  if (CONVERSIONS[from]?.includes(to)) return null;
  if (from === 'service_request' && to === 'major_incident') {
    return 'A service request cannot be declared a major incident — raise the incident it caused and declare that.';
  }
  const noun = TYPE_LABEL[from] ?? from;
  return `A ${noun.toLowerCase()} cannot be converted to a ${(TYPE_LABEL[to] ?? to).toLowerCase()} — it has its own plan, lifecycle and history.`;
}
